import React, { useMemo } from 'react'

import Highcharts from 'highcharts'
import HCMore from 'highcharts/highcharts-more'
import SolidGauge from 'highcharts/modules/solid-gauge'
import { merge } from 'lodash'
import { useTheme } from 'styled-components'

import {
  getComparatorValue,
  getDatapointsCalculatedValue,
} from 'util/datapointCalculationFunctions'
import { useComparatorsPayload, useDatapointsForTimeRangePayload } from 'util/hooks'
import { useLocaleNumber } from 'util/numbers'
import { useQueryData } from 'services/data'

import { useWidgetDataRefetch } from 'components/Dashboard/Widget/hooks.js'
import { getDatapointErrors } from 'components/Widgets/functions'
import { CircleGaugeUi } from 'components/Widgets/Gauge/CircleGauge/styled'

import { chartOptions, getSerie } from '../chartOptions'

HCMore(Highcharts)
SolidGauge(Highcharts)

const CircleGauge = ({
  datapoints,
  value,
  comparators,
  timeRange,
  comparisonPeriod,
  conversionUnit,
  decimals = 2,
  showPercentage = false,
  showInvertedValues = false,
  method,
  comparatorsMethod,
  showDelta = false,
  dataProps = {},
  preview = { gauge: { series: [] } },
  newSocketValue,
  customLogic,
  icon,
  color,
  trackColor,
  caption = '',
  defaultCalculationMethod,
  comparatorsCalculationMethod,
  ...props
}) => {
  const theme = useTheme()
  const localizeNumber = useLocaleNumber()
  const { isPreviewMode, isEditMode } = dataProps

  const datapointsPayload = useDatapointsForTimeRangePayload({
    datapoints,
    timeRange,
    groupConversionUnit: conversionUnit,
  })

  const { data, error, loading, refetch } = useQueryData({
    service: 'datapoints',
    payload: {
      datapoints: datapointsPayload,
      widgetType: 'circleGauge',
    },
    deps: [datapointsPayload],
    skip: !datapoints?.length,
  })

  const { values, comparatorDatapoints } = useComparatorsPayload({
    datapoints,
    comparators,
    timeRange: comparisonPeriod || timeRange,
    groupConversionUnit: conversionUnit,
  })

  const { data: comparatorsData, loading: comparatorsLoading } = useQueryData({
    service: 'datapoints',
    payload: {
      datapoints: comparatorDatapoints,
      widgetType: 'circleGaugeComparator',
    },
    deps: [comparatorDatapoints],
    skip: !comparatorDatapoints?.length,
  })

  useWidgetDataRefetch(refetch, newSocketValue, loading, isPreviewMode, isEditMode)

  const validData = data?.data?.datapoints

  const roundedMaximum = getComparatorValue({
    comparators,
    comparatorValues: values,
    comparatorsData,
    method: comparatorsMethod,
    decimals,
    calculationMethod: comparatorsCalculationMethod,
    defaultValue: 100,
  })

  const { series, datapointChartProps, errors } = useMemo(() => {
    const errors = getDatapointErrors(datapoints)
    if (!validData?.length) {
      return isPreviewMode
        ? { series: error ? [] : preview.gauge.series, datapointChartProps: {}, errors }
        : { datapointChartProps: {}, errors }
    }

    const value = getDatapointsCalculatedValue(validData, {
      method,
      customLogic,
      decimals,
      calculationMethod: defaultCalculationMethod,
    })
    const percentageValue = value === 0 || roundedMaximum === 0 ? 0 : (value / roundedMaximum) * 100
    const percentage =
      value === 0 || roundedMaximum === 0
        ? 0
        : showInvertedValues
        ? 100 - percentageValue
        : percentageValue

    const serieColor = color || theme.color.main
    const series = [
      getSerie(
        {
          value,
          color: serieColor,
          showInvertedValues,
          showPercentage,
          percentage,
          unit: validData[0].conversionFactor?.endUnit.name || validData[0].unit,
          max: roundedMaximum,
          caption,
          icon,
        },
        localizeNumber
      ),
    ]
    const datapointChartProps = {
      yAxis: {
        max: roundedMaximum,
      },
    }
    return { series, datapointChartProps, errors }
  }, [
    localizeNumber,
    validData,
    roundedMaximum,
    color,
    customLogic,
    decimals,
    preview.gauge.series,
    theme,
    showInvertedValues,
    showPercentage,
    isPreviewMode,
    caption,
    method,
    datapoints,
    error,
    icon,
    defaultCalculationMethod,
  ])

  const globalChartOptions = chartOptions({
    series,
    color: color || theme.color.main,
    caption,
    trackColor,
  })

  const options = merge({}, globalChartOptions, datapointChartProps)

  return (
    <CircleGaugeUi
      {...props}
      icon={icon}
      color={color}
      options={options}
      loading={{
        loading: loading || comparatorsLoading,
        icon: preview.icon,
      }}
      inlineErrors={errors}
      error={error}
    />
  )
}

export default CircleGauge
